/**
 * RequireAuth — portal route guard
 *
 * Redirects unauthenticated users to /login, preserving the intended path.
 * Shows a branded loading spinner while the session is being validated.
 */

import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Anchor } from 'lucide-react';
import { canUsePortalPermission, type PortalPermission } from '@/lib/portal-access';

interface RequireAuthProps {
  children: ReactNode;
  permission?: PortalPermission;
}

export function RequireAuth({ children, permission }: RequireAuthProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-brand-900">
        <div className="flex flex-col items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-brand-500 to-ocean-500 flex items-center justify-center">
            <Anchor className="w-8 h-8 text-white" />
          </div>
          <div className="w-8 h-8 border-4 border-brand-300 border-t-white rounded-full animate-spin" />
          <p className="text-white/70 text-sm">Verifying session…</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (permission && !canUsePortalPermission(user?.role, permission, user?.portalPermissions)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="max-w-md rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
          <p className="text-sm font-semibold text-slate-900">This portal page is not enabled for your role.</p>
          <p className="mt-2 text-sm text-slate-500">Ask your customer portal admin to update your role if you need access.</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
